import * as React from 'react';
import { useGstRegister, useMarkGstFiled, type GstRegisterItem, type GstStatus } from '../network/tax.api.js';
import { DataTable } from '@repo/web-list';
import { defineListPage } from '@repo/list';
import { StatusBadge } from '../../../components/ui/StatusBadge.js';
import { AmountDisplay } from '../../../components/ui/AmountDisplay.js';
import { StatCard } from '../../../components/layout/StatCard.js';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.js';
import { Button } from '../../../components/ui/Button.js';
import { useToast } from '../../../components/ui/Toast.js';

interface GstRegisterProps {
  schemeId: string;
  onSchemeIdChange: (id: string) => void;
  schemes: any[];
  canUpdate?: boolean;
}

const STATUS_CONFIG: Record<GstStatus, { label: string; variant: 'warning' | 'info' | 'success' | 'danger' }> = {
  PENDING: { label: 'Pending Filing', variant: 'warning' },
  FILED: { label: 'Filed', variant: 'success' },
};

export function GstRegister({ schemeId, onSchemeIdChange, schemes, canUpdate = true }: GstRegisterProps) {
  const [page, setPage] = React.useState(1);
  const limit = 20;
  const { toast } = useToast();

  const { data, isLoading, error, refetch } = useGstRegister({
    schemeId: schemeId || undefined,
    page,
    limit,
  });

  const markFiled = useMarkGstFiled();
  const [confirmId, setConfirmId] = React.useState<string | null>(null);

  const handleMarkFiled = async () => {
    if (!confirmId) return;
    try {
      await markFiled.mutateAsync(confirmId);
      toast({ type: 'success', title: 'GST Marked Filed', description: 'GST invoice status updated to Filed.' });
      setConfirmId(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to mark GST as filed';
      toast({ type: 'error', title: 'Error', description: msg });
      console.error(err);
    }
  };

  // Calculate local page aggregates for stats
  const items = data?.data ?? [];
  const totalGst = items.reduce((sum, item) => sum + item.totalTax, 0);
  const totalCgst = items.reduce((sum, item) => sum + item.cgstAmount, 0);
  const totalSgst = items.reduce((sum, item) => sum + item.sgstAmount, 0);
  const totalIgst = items.reduce((sum, item) => sum + item.igstAmount, 0);
  const pendingFilingsCount = items.filter((item) => item.status === 'PENDING').length;

  const config = defineListPage<GstRegisterItem>({
    variant: 'data-table',
    resource: 'gst',
    columns: [
      { id: 'invoiceNumber', header: 'Invoice Number', accessor: 'invoiceNo', renderType: 'code' },
      { id: 'customerName', header: 'Customer Name', accessor: 'customerName', renderType: 'text' },
      { id: 'gstin', header: 'GSTIN', accessor: 'customerGstin', renderType: 'code' },
      { id: 'invoiceDate', header: 'Invoice Date', accessor: 'invoiceDate', renderType: 'date' },
      { id: 'taxableValue', header: 'Taxable Value', accessor: 'taxableValue', renderType: 'money', numeric: true },
      { id: 'cgstAmount', header: 'CGST', accessor: 'cgstAmount', renderType: 'money', numeric: true },
      { id: 'sgstAmount', header: 'SGST', accessor: 'sgstAmount', renderType: 'money', numeric: true },
      { id: 'igstAmount', header: 'IGST', accessor: 'igstAmount', renderType: 'money', numeric: true },
      { id: 'totalGst', header: 'Total GST', accessor: 'totalTax', renderType: 'money', numeric: true },
      { id: 'status', header: 'Status', accessor: 'status', renderType: 'text' },
    ],
  });

  return (
    <div className="flex flex-col gap-6">
      {/* KPI Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Total GST" value={<AmountDisplay paise={totalGst} />} />
        <StatCard label="CGST" value={<AmountDisplay paise={totalCgst} />} />
        <StatCard label="SGST" value={<AmountDisplay paise={totalSgst} />} />
        <StatCard label="IGST" value={<AmountDisplay paise={totalIgst} />} />
        <StatCard label="Pending Filings" value={String(pendingFilingsCount)} />
      </div>

      {/* Main Data Table */}
      <DataTable
        config={config}
        data={items}
        total={data?.meta?.total ?? 0}
        page={page}
        limit={limit}
        toolbarActions={
          <>
            <select
              value={schemeId}
              onChange={(e) => {
                onSchemeIdChange(e.target.value);
                setPage(1);
              }}
              className="bg-surface-1 border border-border text-text-primary px-3 py-1.5 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-accent-500 font-semibold"
            >
              <option value="">All Schemes</option>
              {schemes.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.displayName || s.schemeNumber}
                </option>
              ))}
            </select>
            <Button
              variant="ghost"
              onClick={() => void refetch()}
              className="text-xs font-semibold text-accent-500 hover:text-accent-600 transition-colors bg-transparent hover:bg-transparent"
            >
              Refresh
            </Button>
          </>
        }
        onPageChange={setPage}
        loading={isLoading}
        error={error ? 'Failed to load GST register.' : undefined}
        onRetry={refetch}
        customRenderers={{
          status: (row: GstRegisterItem) => {
            const s = row.status;
            const cfg = STATUS_CONFIG[s] ?? { label: s, variant: 'info' as const };
            return <StatusBadge status={cfg.variant} label={cfg.label} />;
          },
          actions: (row: GstRegisterItem) => {
            if (!canUpdate) {
              return <span className="text-xs text-text-muted">Read Only</span>;
            }
            if (row.status === 'PENDING') {
              return (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmId(row.id);
                  }}
                >
                  Mark Filed
                </Button>
              );
            }
            return <span className="text-xs font-medium text-success-text">Filed</span>;
          },
        }}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmId !== null}
        title="Confirm GST Filing"
        description="Are you sure you want to mark this GST invoice as filed? This action cannot be undone."
        confirmLabel="Mark Filed"
        onConfirm={() => void handleMarkFiled()}
        onCancel={() => setConfirmId(null)}
        loading={markFiled.isPending}
      />
    </div>
  );
}
